import React, { useEffect, useCallback, useReducer } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Box, Typography, TextField, IconButton, InputAdornment, Grid, TableSortLabel, CircularProgress, MenuItem } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { terminalsService } from '../../services/terminalsService';
import { CategoryType } from '../../models/enums/categoryType';
import { PermissionType } from '../../models/enums/permissionType';
import { ShowForPermission } from '../common/permissions/ShowForPermission';
import TmPlus from '../../icons/TmPlus';
import { TerminalShortInfoDto } from '../../models/terminals/terminalShortInfoDto';
import { ROUTES } from '../../constants/routes';
import { EditTerminalModal } from './edit/EditTerminalModal';
import AcceptLabel from '../common/labels/AcceptLabel';
import DenyLabel from '../common/labels/DenyLabel';
import { SortingOrder } from '../../models/enums/sortingOrder';
import { CompanyShortDto } from '../../models/companies/companyShortDto';
import CompanyAutocomplete from '../common/CompanyAutocomplete';
import { filterStorageService } from '../../services/filterStorageService';
import { FilterSection } from '../../models/enums/filterSection';

const PAGE_REFRESH_INTERVAL = 10000;

const initialState = {
    terminals: [] as TerminalShortInfoDto[],
    page: 0,
    rowsPerPage: 15,
    dataCount: 0,
    loading: true,
    isAddPopupOpen: false,
    filter: {
        name: '' as string,
        number: '' as string | null,
        company: null as CompanyShortDto | null,
        niiat: 'all' as 'all' | 'yes' | 'no',
    },
    sortConfig: {
        sortColumn: 'id',
        sortOrder: SortingOrder.Asc,
        loadingColumn: null as string | null,
    },
    shouldFetch: true,
};

type State = typeof initialState;

type Action =
    | { type: 'SET_TERMINALS'; payload: { terminals: TerminalShortInfoDto[]; dataCount: number } }
    | { type: 'SET_PAGE'; payload: number }
    | { type: 'SET_ROWS_PER_PAGE'; payload: number }
    | { type: 'SET_FILTER'; payload: Partial<State['filter']> }
    | { type: 'SET_SORT'; payload: { column: string; order: SortingOrder } }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_SHOULD_FETCH'; payload: boolean }
    | { type: 'TOGGLE_ADD_POPUP' };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'SET_TERMINALS':
            return {
                ...state,
                terminals: action.payload.terminals,
                dataCount: action.payload.dataCount,
                loading: false,
                sortConfig: { ...state.sortConfig, loadingColumn: null },
                shouldFetch: false,
            };
        case 'SET_PAGE':
            return { ...state, page: action.payload, loading: true, shouldFetch: true };
        case 'SET_ROWS_PER_PAGE':
            return { ...state, rowsPerPage: action.payload, page: 0, loading: true, shouldFetch: true };
        case 'SET_FILTER':
            return {
                ...state,
                filter: { ...state.filter, ...action.payload },
                page: 0,
                loading: true,
                shouldFetch: true,
            };
        case 'SET_SORT':
            return {
                ...state,
                sortConfig: {
                    sortColumn: action.payload.column,
                    sortOrder: action.payload.order,
                    loadingColumn: action.payload.column,
                },
                loading: true,
                shouldFetch: true,
            };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_SHOULD_FETCH':
            return { ...state, shouldFetch: action.payload };
        case 'TOGGLE_ADD_POPUP':
            return { ...state, isAddPopupOpen: !state.isAddPopupOpen };
        default:
            return state;
    }
};

export const TerminalsTable = () => {
    const [state, dispatch] = useReducer(reducer, initialState, (baseState) => ({
        ...baseState,
        filter: filterStorageService.loadFilter(FilterSection.Terminals, baseState.filter),
    }));

    const navigate = useNavigate();

    const fetchTerminals = useCallback(() => {
        if (!state.shouldFetch) {
            return;
        }

        dispatch({ type: 'SET_LOADING', payload: true });
        terminalsService.getByFilter({
            page: state.page,
            pageSize: state.rowsPerPage,
            name: state.filter.name || undefined,
            number: state.filter.number || undefined,
            companyId: state.filter.company ? state.filter.company.id : undefined,
            isNiiatIntegrationEnabled:
                state.filter.niiat === 'yes' ? true :
                state.filter.niiat === 'no' ? false :
                undefined,
            sortColumn: state.sortConfig.sortColumn,
            sortOrder: state.sortConfig.sortOrder,
        })
            .then(r => {
                dispatch({
                    type: 'SET_TERMINALS',
                    payload: { terminals: r.data.data.items, dataCount: r.data.data.count },
                });
            })
            .catch(e => console.log(e))
            .finally(() => {
                dispatch({ type: 'SET_LOADING', payload: false });
            });
    }, [state.shouldFetch, state.page, state.rowsPerPage, state.filter, state.sortConfig.sortColumn, state.sortConfig.sortOrder]);

    useEffect(() => {
        if (state.shouldFetch) {
            fetchTerminals();
        }
        const interval = setInterval(fetchTerminals, PAGE_REFRESH_INTERVAL);
        return () => clearInterval(interval); // Clear interval on component unmount
    }, [state.shouldFetch, fetchTerminals]);

    useEffect(() => {
        filterStorageService.saveFilter(FilterSection.Terminals, state.filter);
    }, [state.filter]);

    const handleChangePage = (_event: any, newPage: number) => {
        dispatch({ type: 'SET_PAGE', payload: newPage });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        dispatch({ type: 'SET_ROWS_PER_PAGE', payload: parseInt(event.target.value, 10) });
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({
            type: 'SET_FILTER',
            payload: { [event.target.name]: event.target.value } as Partial<State['filter']>,
        });
    };

    const handleCompanyChange = (newCompany: CompanyShortDto | null) => {
        dispatch({ type: 'SET_FILTER', payload: { company: newCompany } });
    };

    const handleAddPopupToggle = () => {
        dispatch({ type: 'TOGGLE_ADD_POPUP' });
    };

    const handleRowClick = (event: React.MouseEvent, terminalId: number) => {
        const url = ROUTES.terminals.idPath.replace(':id', String(terminalId));
        navigate(url);
    };

    const handleSort = (column: string) => {
        const isAsc = state.sortConfig.sortColumn === column && state.sortConfig.sortOrder === SortingOrder.Asc;
        dispatch({
            type: 'SET_SORT',
            payload: { column, order: isAsc ? SortingOrder.Desc : SortingOrder.Asc },
        });
    };

    const renderOnlineColumn = (isOnline?: boolean): React.ReactNode => {
        if (isOnline === undefined || isOnline === null) {
            return '';
        }
        return isOnline ? <AcceptLabel>Online</AcceptLabel> : <DenyLabel>Offline</DenyLabel>
    }

    return (
        <Box>
            <Grid container pb={1} mt={2.5}>
                <Grid item xs={10} style={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                        label="Серийный номер"
                        name="number"
                        value={state.filter.number || ''}
                        onChange={handleFilterChange}
                        variant="outlined"
                        margin="dense"
                        size="small"
                        style={{ width: 200, marginRight: 10 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => dispatch({ type: 'SET_PAGE', payload: 0 })}>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            style: {
                                borderRadius: "10px",
                            }
                        }}
                    />

                    <TextField
                        label="Название"
                        name="name"
                        value={state.filter.name}
                        onChange={handleFilterChange}
                        variant="outlined"
                        margin="dense"
                        size="small"
                        style={{ width: 200, marginRight: 10 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => dispatch({ type: 'SET_PAGE', payload: 0 })}>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            style: {
                                borderRadius: "10px",
                            }
                        }}
                    />

                    <CompanyAutocomplete
                        company={state.filter.company ?? null}
                        onCompanyChange={handleCompanyChange}
                        style={{ width: 200, marginRight: 10 }}
                    />

                    <TextField
                        select
                        label="НИИАТ"
                        name="niiat"
                        value={state.filter.niiat}
                        onChange={handleFilterChange}
                        variant="outlined"
                        margin="dense"
                        size="small"
                        style={{ width: 200, marginRight: 10 }}
                        SelectProps={{ style: { borderRadius: "10px" } }}
                    >
                        <MenuItem value="all">Все</MenuItem>
                        <MenuItem value="yes">Да</MenuItem>
                        <MenuItem value="no">Нет</MenuItem>
                    </TextField>
                </Grid>
                <Grid item xs={2} style={{ display: 'inline-block', alignItems: 'center' }}>
                    <ShowForPermission catalog={CategoryType.Terminals} permission={PermissionType.Add}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton sx={{ display: "flex" }} className="list-item" onClick={handleAddPopupToggle} >
                                <TmPlus fill="none" stroke="white" />&nbsp;
                                <Typography fontWeight="bold" pr={1}>
                                    &nbsp;Добавить новый терминал
                                </Typography>
                            </IconButton>
                        </Box>
                    </ShowForPermission>
                </Grid>
            </Grid>

            <TableContainer sx={{ '.MuiTableCell-root': { py: 1.5 } }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '10%' }}>
                                <TableSortLabel
                                    active={state.sortConfig.sortColumn === 'id'}
                                    direction={state.sortConfig.sortColumn === 'id' ? state.sortConfig.sortOrder : SortingOrder.Asc}
                                    onClick={() => handleSort('id')}
                                    IconComponent={state.sortConfig.loadingColumn === 'id' ? () => <CircularProgress size={12} sx={{ color: '#9588e8' }} /> : undefined}
                                >
                                    <Typography variant="body1" sx={{ color: "#7c7e92" }}>ID</Typography>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                <TableSortLabel
                                    active={state.sortConfig.sortColumn === 'number'}
                                    direction={state.sortConfig.sortColumn === 'number' ? state.sortConfig.sortOrder : SortingOrder.Asc}
                                    onClick={() => handleSort('number')}
                                    IconComponent={state.sortConfig.loadingColumn === 'number' ? () => <CircularProgress size={12} sx={{ color: '#9588e8' }} /> : undefined}
                                >
                                    <Typography variant="body1" sx={{ color: "#7c7e92" }}>Серийный номер</Typography>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ width: '20%' }}>
                                <TableSortLabel
                                    active={state.sortConfig.sortColumn === 'name'}
                                    direction={state.sortConfig.sortColumn === 'name' ? state.sortConfig.sortOrder : SortingOrder.Asc}
                                    onClick={() => handleSort('name')}
                                    IconComponent={state.sortConfig.loadingColumn === 'name' ? () => <CircularProgress size={12} sx={{ color: '#9588e8' }} /> : undefined}
                                >
                                    <Typography variant="body1" sx={{ color: "#7c7e92" }}>Название</Typography>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                <TableSortLabel
                                    active={state.sortConfig.sortColumn === 'companies'}
                                    direction={state.sortConfig.sortColumn === 'companies' ? state.sortConfig.sortOrder : SortingOrder.Asc}
                                    onClick={() => handleSort('companies')}
                                    IconComponent={state.sortConfig.loadingColumn === 'companies' ? () => <CircularProgress size={12} sx={{ color: '#9588e8' }} /> : undefined}
                                >
                                    <Typography variant="body1" sx={{ color: "#7c7e92" }}>Клиенты</Typography>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ width: '20%' }}><Typography variant="body1" sx={{ color: "#7c7e92" }}>Адрес</Typography></TableCell>
                            <TableCell sx={{ width: '10%' }}>
                                <TableSortLabel
                                    active={state.sortConfig.sortColumn === 'isActive'}
                                    direction={state.sortConfig.sortColumn === 'isActive' ? state.sortConfig.sortOrder : SortingOrder.Asc}
                                    onClick={() => handleSort('isActive')}
                                    IconComponent={state.sortConfig.loadingColumn === 'isActive' ? () => <CircularProgress size={12} sx={{ color: '#9588e8' }} /> : undefined}
                                >
                                    <Typography variant="body1" sx={{ color: "#7c7e92" }}>Статус</Typography>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                <TableSortLabel
                                    active={state.sortConfig.sortColumn === 'isOnline'}
                                    direction={state.sortConfig.sortColumn === 'isOnline' ? state.sortConfig.sortOrder : SortingOrder.Asc}
                                    onClick={() => handleSort('isOnline')}
                                    IconComponent={state.sortConfig.loadingColumn === 'isOnline' ? () => <CircularProgress size={12} sx={{ color: '#9588e8' }} /> : undefined}
                                >
                                    <Typography variant="body1" sx={{ color: "#7c7e92" }}>Онлайн</Typography>
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {state.terminals.map((terminal) => (
                            <TableRow
                                key={terminal.id}
                                onClick={(event) => handleRowClick(event, terminal.id)}
                                sx={{ '&:hover': { cursor: 'pointer', backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                            >
                                <TableCell sx={{ fontWeight: "bold" }}>{terminal.id}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{terminal.number}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{terminal.name}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{terminal.companies.length > 0 ? terminal.companies.map(company => company.name).join(', ') : '<Отсутствует>'}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{terminal.address}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{terminal.isActive ? <AcceptLabel>Активен</AcceptLabel> : <DenyLabel>Не активен</DenyLabel>}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{renderOnlineColumn(terminal.isOnline)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Всего: {state.dataCount}</Typography>
                <TablePagination
                    rowsPerPageOptions={[15, 30, 50]}
                    component="div"
                    count={state.dataCount}
                    rowsPerPage={state.rowsPerPage}
                    page={state.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={"Строк на странице:"}
                    labelDisplayedRows={({ from, to, count }: any) => `Показано ${from}-${to} из ${count}`}
                />
            </Box>
            <EditTerminalModal onClose={handleAddPopupToggle} onRefresh={fetchTerminals} isAdding={true} open={state.isAddPopupOpen} />
        </Box>
    );
};